import { Component } from '@theme/component';

/**
 * Butonul de contact din colțul de jos: un buton rotund care deschide două opțiuni (WhatsApp / apel).
 * Se ridică deasupra barei sticky „adaugă în coș” când aceasta acoperă colțul (pe mobil bara e pe toată lățimea).
 * Scoate și butonul de telefon al aplicației Worldphoneize, adăugat prin script extern (altfel ar fi două butoane).
 *
 * @typedef {{ toggle?: HTMLButtonElement, panel?: HTMLElement }} ContactRefs
 * @extends {Component<ContactRefs>}
 */
class PerdeleContact extends Component {
  requiredRefs = ['toggle', 'panel'];

  /** @type {AbortController | undefined} */
  #abort;

  /** @type {MutationObserver | undefined} */
  #appWatcher;

  #frame = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    document.addEventListener('click', (event) => {
      if (event.target instanceof Node && !this.contains(event.target)) this.close(false);
    }, { signal });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.refs.panel.hidden) this.close(true);
    }, { signal });
    const lift = () => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => this.#liftAboveStickyBar());
    };
    window.addEventListener('resize', lift, { passive: true, signal });
    window.addEventListener('scroll', lift, { passive: true, signal });
    document.querySelector('.page-wrapper')?.addEventListener('scroll', lift, { passive: true, signal });
    // Bara sticky apare/dispare la derulare și își schimbă `data-stuck` după eveniment: recalculăm și după tranziție.
    document.addEventListener('transitionend', lift, { signal });
    this.#removeAppButton();
    this.#appWatcher = new MutationObserver(() => this.#removeAppButton());
    this.#appWatcher.observe(document.body, { childList: true });
    setTimeout(() => this.#appWatcher?.disconnect(), 20000);
    lift();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#appWatcher?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #removeAppButton() {
    document.getElementById('worldphonecallbutton')?.remove();
    document.getElementById('worldphonecallbuttoncta')?.remove();
  }

  #liftAboveStickyBar() {
    const bar = document.querySelector('.sticky-add-to-cart__bar[data-stuck="true"]');
    let lift = 0;
    if (bar) {
      const barRect = bar.getBoundingClientRect();
      const mine = this.getBoundingClientRect();
      const overlapsHorizontally = barRect.left < mine.right && barRect.right > mine.left;
      if (overlapsHorizontally) lift = Math.max(0, window.innerHeight - barRect.top);
    }
    this.style.setProperty('--perdele-lift', `${lift}px`);
  }

  toggle() {
    if (this.refs.panel.hidden) this.#open();
    else this.close(false);
  }

  #open() {
    this.refs.panel.hidden = false;
    this.refs.toggle.setAttribute('aria-expanded', 'true');
  }

  /** @param {boolean} restoreFocus */
  close(restoreFocus) {
    if (this.refs.panel.hidden) return;
    this.refs.panel.hidden = true;
    this.refs.toggle.setAttribute('aria-expanded', 'false');
    if (restoreFocus) this.refs.toggle.focus();
  }
}

if (!customElements.get('perdele-contact')) customElements.define('perdele-contact', PerdeleContact);
